// Per-run posse stats, counted on every client from the reliable events it already receives
// (the server broadcasts every event to every cowboy), so the end-of-run Paperwork card needs no
// new RPCs and every client shows the same numbers. A cowboy who drops in late only misses what
// happened before they arrived.

import { Ev, GameEvent } from '../../sim/codec';
import { EType } from '../../sim/defs';

export interface CowboyLine {
  id: number;
  kills: number;
  detonations: number;
  /** your detonation on a robot a teammate primed */
  teamDets: number;
  /** a teammate detonated a robot you primed */
  assists: number;
  revives: number;
  downs: number;
  bestChain: number;
  bossKills: number;
  /** friendly fire: damage put into teammates, and teammates downed by it */
  ffDealt: number;
  ffDowns: number;
  /** crate weapons requisitioned */
  crates: number;
}

export interface Award { id: number; title: string; why: string }

export class RunStats {
  lines = new Map<number, CowboyLine>();
  waves = 0;
  tier = 0;
  startedAt = 0;
  /** who primed each robot, so a cross detonation can credit the primer */
  private primedBy = new Map<number, number>();

  reset(now: number) { this.lines.clear(); this.primedBy.clear(); this.waves = 0; this.tier = 0; this.startedAt = now; }

  line(id: number): CowboyLine {
    let l = this.lines.get(id);
    if (!l) { l = { id, kills: 0, detonations: 0, teamDets: 0, assists: 0, revives: 0, downs: 0, bestChain: 0, bossKills: 0, ffDealt: 0, ffDowns: 0, crates: 0 }; this.lines.set(id, l); }
    return l;
  }

  primer(eid: number) { return this.primedBy.get(eid) ?? -1; }

  /** Feed every server event. Returns nothing; read `lines` when the run ends. */
  onEvent(e: GameEvent) {
    const f = e.f;
    switch (e.t) {
      case Ev.Prime: this.primedBy.set(f[0], f[1]); break;
      case Ev.Unprime: this.primedBy.delete(f[0]); break;
      case Ev.Detonate: {
        const [eid, pid, , , , , , , cross] = f;
        const l = this.line(pid); l.detonations++;
        if (cross) {
          l.teamDets++;
          const by = this.primedBy.get(eid);
          if (by !== undefined && by !== pid) this.line(by).assists++;
        }
        this.primedBy.delete(eid);
        break;
      }
      case Ev.Chain: { const l = this.line(f[0]); l.bestChain = Math.max(l.bestChain, f[1]); break; }
      case Ev.EnemyDie: {
        const [eid, type, , , , killer] = f;
        this.primedBy.delete(eid);
        if (killer > 0 && killer < 255) { const l = this.line(killer); l.kills++; if (type === EType.MetalBrute) l.bossKills++; }
        break;
      }
      case Ev.Down: this.line(f[0]).downs++; break;
      case Ev.FriendlyHit: {
        const [victim, by, dmg, downed] = f;
        if (by === victim) break;
        const l = this.line(by); l.ffDealt += dmg; if (downed) l.ffDowns++;
        break;
      }
      case Ev.Crate: if (f[5] === 0 && f[6] !== 255) this.line(f[6]).crates++; break;
      case Ev.Revive: this.line(f[1]).revives++; break;
      case Ev.Wave: if (f[1] === 1) { this.waves = Math.max(this.waves, f[0]); this.tier = Math.max(this.tier, f[2]); } break;
    }
  }

  /** Joke awards for the Paperwork card. Ties go to whoever's id sorts first so every client agrees. */
  awards(): Award[] {
    const all = [...this.lines.values()].sort((a, b) => a.id - b.id);
    if (all.length < 2) return [];
    const out: Award[] = [];
    const best = (score: (l: CowboyLine) => number) => {
      let top: CowboyLine | null = null, s = 0;
      for (const l of all) { const v = score(l); if (v > s) { s = v; top = l; } }
      return top;
    };
    const eom = best((l) => l.kills + l.detonations * 2 + l.teamDets * 2 + l.revives * 3);
    if (eom) out.push({ id: eom.id, title: 'EMPLOYEE OF THE MONTH', why: 'Highest throughput. Framed photo pending.' });
    const mm = best((l) => l.assists);
    if (mm) out.push({ id: mm.id, title: 'MIDDLE MANAGEMENT', why: `Set up ${mm.assists} for somebody else to finish.` });
    const sam = best((l) => l.revives);
    if (sam) out.push({ id: sam.id, title: 'GOOD SAMARITAN CLAUSE', why: `${sam.revives} revive${sam.revives === 1 ? '' : 's'}. Not billable.` });
    const lia = best((l) => l.downs);
    if (lia) out.push({ id: lia.id, title: 'LIABILITY', why: `Down ${lia.downs} time${lia.downs === 1 ? '' : 's'}. Premiums adjusted.` });
    const wi = best((l) => l.ffDealt + l.ffDowns * 100);
    if (wi) out.push({ id: wi.id, title: 'WORKPLACE INCIDENT', why: wi.ffDowns ? `Downed ${wi.ffDowns} of their own posse. HR notified.` : `Put ${wi.ffDealt} damage into their own posse. HR notified.` });
    const cap = best((l) => l.crates);
    if (cap && cap.crates >= 2) out.push({ id: cap.id, title: 'CAPITAL EXPENDITURE', why: `Requisitioned ${cap.crates} crates. Receipts not found.` });
    return out;
  }
}
